import React from "react";

interface StepIndicatorProps {
  currentStep: string;
  logsGenerated?: boolean;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ currentStep, logsGenerated = false }) => {
  const steps = [
    { key: 'upload', label: 'Upload File' },
    { key: 'uploading', label: 'Processing' },
    { key: 'preview', label: 'Preview' },
    { key: 'executing', label: 'Execution' },
    { key: 'processing', label: 'Processing Transactions' },
    { key: 'logs', label: 'Logs' }
  ];

  const currentStepIndex = steps.findIndex(step => step.key === currentStep);

  // Calculate progress percentage - if logs are generated, consider all steps complete
  const progressPercentage = logsGenerated
    ? 100
    : ((currentStepIndex + 1) / steps.length) * 100;

  return (
    <div className="step-indicator bg-white dark:bg-neutral-900 rounded-xl p-6 mb-8 shadow-md border border-neutral-200 dark:border-neutral-800">
      <nav aria-label="Step progress">
        <ol className="flex items-center justify-between gap-2">
          {steps.map((step, index) => {
            // Determine step state
            let stepClass = '';
            if (logsGenerated && step.key === 'logs') {
              stepClass = 'completed'; // Mark logs as completed when generated
            } else if (index === currentStepIndex) {
              stepClass = 'active';
            } else if (index < currentStepIndex) {
              stepClass = 'completed';
            }

            const circleClasses = stepClass === 'active'
              ? 'bg-primary-500 text-white shadow-[0_0_0_4px_rgba(0,123,255,0.2)]'
              : stepClass === 'completed'
              ? 'bg-green-600 text-white'
              : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400';

            return (
              <li
                key={step.key}
                className={`flex flex-col items-center flex-1 text-center text-sm ${
                  stepClass === 'active' ? 'font-semibold text-primary-600 dark:text-primary-400' :
                  stepClass === 'completed' ? 'text-green-600 dark:text-green-400' :
                  'text-neutral-500 dark:text-neutral-400'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold mb-2 transition-all ${circleClasses}`}>
                  {stepClass === 'completed' ? '\u2713' : index + 1}
                </div>
                {step.label}
              </li>
            );
          })}
        </ol>
      </nav>
      <div className="mt-5 h-1 bg-neutral-200 dark:bg-neutral-700 rounded-sm overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-primary-500 to-green-500 rounded-sm transition-all duration-500"
          style={{ width: `${progressPercentage}%` }}
        ></div>
      </div>
    </div>
  );
};

export default StepIndicator;
